<template>
  <div :class="['comments-section-wrapper', { 'embedded': embedded }]">
    <!-- Comments Section Container -->
    <div :class="['comments-section-container', { 'embedded': embedded }]">
      <!-- Comments Header -->
      <!-- <div v-if="shouldShowTitle" class="comments-section-header">
        <h5 class="comments-section-title">
          {{ getCommentsTitle() }}
        </h5>
      </div> -->
      
      <!-- Comments List -->
      <comments-list
        ref="commentsList"
        :source-key="sourceKey"
        :can-moderate="canModerate"
        :morph-type="morphType"
        :item-type="itemType"
        :leaf="leaf"
        :embedded="embedded"
        :refresh-key="refreshKey"
        :comment-key-highlighted="commentKeyHighlighted"
        :sub-comment-key-highlighted="subCommentKeyHighlighted"
        @delete-comment="handleDeleteComment"
        @commented="handleCommented"
        @moderation-complete="handleModerationComplete"
      />
      
      <!-- Comment Form -->
      <comment-form
        :source-key="sourceKey"
        :morph-type="morphType"
        :item-type="itemType"
        :leaf="leaf"
        @created="handleCommentCreated"
        @cancel="handleCommentCancel"
        @update="handleCommentUpdate"
      />
    </div>
  </div>
</template>

<script>
import CommentsList from './CommentsList.vue';
import CommentForm from './CommentForm.vue';

export default {
  name: 'CommentsSection',
  components: {
    CommentsList,
    CommentForm,
  },
  props: {
    /**
     * The unique key of the item that comments belong to (e.g., post key, forum key)
     */
    sourceKey: {
      type: String,
      required: true,
    },
    /**
     * The type of content being commented on (e.g., 'post', 'forum', 'answer')
     */
    morphType: {
      type: String,
      required: true,
    },
    /**
     * The item type for API calls (e.g., 'posts', 'forums', 'comments')
     */
    itemType: {
      type: String,
      default: null,
    },
    /**
     * Whether this is a leaf node (nested comments) or top-level comments
     */
    leaf: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether the current user can moderate comments
     */
    canModerate: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether this component is embedded in another component (affects styling)
     */
    embedded: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether to show the comments title header
     */
    showTitle: {
      type: Boolean,
      default: true,
    },
    /**
     * The key of the comment to highlight
     */
    commentKeyHighlighted: {
      type: String,
      default: null,
    },
    /**
     * The key of the sub-comment to highlight (for nested comments)
     */
    subCommentKeyHighlighted: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      refreshKey: 0,
    };
  },
  computed: {
    shouldShowTitle() {
      return this.showTitle && !this.embedded;
    },
  },
  methods: {
    getCommentsTitle() {
      return this.$t('social.comments-plural');
    },
    handleCommentCreated() {
      this.refreshKey += 1;
      // Find the CommentsList child and call fetchData
      const commentsList = this.$refs.commentsList;
      if (commentsList && typeof commentsList.fetchData === 'function') {
        commentsList.fetchData();
      }
      this.$emit('comment-created');
    },
    handleCommentCancel() {
      this.$emit('comment-cancel');
    },
    handleCommentUpdate() {
      this.$emit('comment-updated');
    },
    handleDeleteComment() {
      this.$emit('comment-deleted');
    },
    handleCommented(targetComment) {
      this.$emit('commented', targetComment);
    },
    handleModerationComplete(data) {
      this.$emit('moderation-complete', data);
    },
  },
  /**
   * Component Events:
   * 
   * @event comment-created - Emitted when a new comment is created
   * @event comment-cancel - Emitted when comment creation is cancelled
   * @event comment-updated - Emitted when a comment is updated
   * @event comment-deleted - Emitted when a comment is deleted
   * @event commented - Emitted when a reply is added to a comment
   * @event moderation-complete - Emitted when moderation action is completed
   */
};
</script>

<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-include';

.comments-section-wrapper {
  margin-bottom: 1.5rem;
}

.comments-section-container {
  background: #ffffff;
  border: 1px solid #e1e5e9;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.2s ease-in-out;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }
}

.comments-section-header {
  padding: 1.25rem 1.5rem 0.75rem;
  border-bottom: 1px solid #f0f2f5;
}

.comments-section-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #6f767f;
  letter-spacing: 0.5px;
}

// Embedded styles
.comments-section-wrapper.embedded {
  margin-bottom: 0;
}

.comments-section-container.embedded {
  background: transparent;
  border: none;
  box-shadow: none;
  border-radius: 0;
  width: 100%;
  
  &:hover {
    box-shadow: none;
  }
}

// Responsive design
@media (max-width: 768px) {
  .comments-section-container {
    border-radius: 8px;
  }
  
  .comments-section-header {
    padding: 1rem 1.25rem 0.5rem;
  }
  
  .comments-section-title {
    font-size: 1rem;
  }
}
</style> 